import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // Load existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Stay in sync when the user signs in/out in another tab or the token refreshes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
      if (_event === 'SIGNED_IN' && session?.user) {
        const u = session.user;
        const provider = u.app_metadata?.provider;
        const createdAt = new Date(u.created_at).getTime();
        const lastSignIn = new Date(u.last_sign_in_at).getTime();
        const isNewUser = Math.abs(createdAt - lastSignIn) < 10000;
        const welcomeKey = `welcome_sent_${u.id}`;
        if (isNewUser && !localStorage.getItem(welcomeKey)) {
          localStorage.setItem(welcomeKey, '1');
          const firstName = provider === 'google'
            ? u.user_metadata?.given_name
            : u.user_metadata?.first_name;
          fetch('/api/auth/user-created', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: u.email, first_name: firstName }),
          }).catch(() => {});
        }
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = (email, password, metadata = {}) =>
    supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: window.location.origin,
      },
    });

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password });

  const signInWithGoogle = (idToken) =>
    supabase.auth.signInWithIdToken({
      provider: 'google',
      token: idToken,
    });

  const signOut = () => supabase.auth.signOut();

  const updateProfile = (data) =>
    supabase.auth.updateUser({ data });

  const resetPassword = (email) =>
    supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });

  const updatePassword = (newPassword) =>
    supabase.auth.updateUser({ password: newPassword });

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);

  return (
    <AuthContext.Provider value={{
      user, session, loading,
      isPasswordRecovery, clearPasswordRecovery,
      signUp, signIn, signInWithGoogle, signOut,
      updateProfile, resetPassword, updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
